import * as Constants from './Constants';
import { PerformanceDatapoint } from 'owa-analytics';
import endImageLoadPerfDataPoint from './endImageLoadPerfDataPoint';
import { ResponseData } from './getUrlResponseData';
import loadFromUrl from './loadFromUrl';
import logDiagnosticData from './logDiagnosticData';
import logImageLoadError from './logImageLoadError';
import { isFeatureEnabled } from 'owa-feature-flags';

// Status code for loading from data uri
const DATAURI_SUCCESS = 'DataUri_Success';
const DATAURI_REQUESTFAILED = 'DataUri_RequestFailed';
const DATAURI_ONERROR = 'DataUri_OnError';

// The begin portion of MIME
const DATAURI_MIME_REGEX = new RegExp('^data:[a-z]+\\/[a-z-]+;', 'i');

/**
 * The DataUri context
 */
export interface DataUriContext {
    imageType: string;
    howLoad: string;
    context: string;
    loaderCode: string;
    originalSrc: string;
    owaDomain: string;
    downloadDomain: string;
    responseData: ResponseData;
    perfDataPoint: PerformanceDatapoint;
}

/**
 * Load image from data uri with content type fixed
 */
export default function loadImageAsDataUri(
    image: HTMLImageElement,
    url: string,
    dataUriContext: DataUriContext,
    onSuccessCallback?: (imageElement: HTMLImageElement) => void,
    onErrorCallback?: (imageElement: HTMLImageElement) => void
) {
    const request: XMLHttpRequest = new XMLHttpRequest();
    request.onload = () => {
        const reader = new FileReader();
        reader.onloadend = () => {
            if (reader.result) {
                // Fixup content type
                const fixedDataUri = (reader.result as string).replace(
                    DATAURI_MIME_REGEX,
                    `data:${dataUriContext.responseData.expectedMime};`
                );
                loadFromUrl(
                    image,
                    fixedDataUri,
                    () =>
                        completeLoadImage(
                            image,
                            dataUriContext,
                            true /* isSuccess */,
                            onSuccessCallback,
                            onErrorCallback
                        ),
                    () =>
                        completeLoadImage(
                            image,
                            dataUriContext,
                            false /* isSuccess */,
                            onSuccessCallback,
                            onErrorCallback,
                            DATAURI_ONERROR
                        )
                );
            }
        };
        reader.readAsDataURL(request.response);
    };

    request.ontimeout = request.onerror = () => {
        completeLoadImage(
            image,
            dataUriContext,
            false /* isSuccess */,
            onSuccessCallback,
            onErrorCallback,
            DATAURI_REQUESTFAILED
        );
    };

    if (image.crossOrigin == Constants.ATTRIBUTE_VALUE_USE_CREDENTIALS) {
        request.withCredentials = true;
    }

    request.open('GET', url, true /*async*/);
    request.responseType = 'blob';
    request.send();
}

function completeLoadImage(
    image: HTMLImageElement,
    dataUriContext: DataUriContext,
    isSuccess: boolean,
    onSuccessCallback?: (imageElement: HTMLImageElement) => void,
    onErrorCallback?: (imageElement: HTMLImageElement) => void,
    errorOverride?: string
) {
    if (isSuccess) {
        if (onSuccessCallback) {
            onSuccessCallback(image);
        }
    } else {
        if (onErrorCallback) {
            onErrorCallback(image);
        }
    }

    const {
        imageType,
        perfDataPoint,
        howLoad,
        context,
        loaderCode,
        originalSrc,
        owaDomain,
        downloadDomain,
        responseData,
    } = dataUriContext;
    const {
        responseCode,
        status,
        contentType,
        detectedFormat,
        first10BytesBase64,
        mimeMatched,
    } = responseData;
    const error = isSuccess ? '' : errorOverride || status || responseCode.toString();
    if (perfDataPoint) {
        endImageLoadPerfDataPoint(
            perfDataPoint,
            imageType,
            howLoad,
            error,
            context,
            loaderCode,
            isSuccess ? DATAURI_SUCCESS : ''
        );
    }

    if (!isSuccess) {
        logImageLoadError(
            imageType /* type */,
            howLoad /* howLoad */,
            error /* error */,
            responseCode.toString() /* statusCode*/,
            context /* context */,
            loaderCode /* loaderCode*/,
            originalSrc /* originalSrc*/,
            contentType /* contentType */,
            owaDomain,
            downloadDomain,
            detectedFormat,
            first10BytesBase64,
            mimeMatched.toString()
        );

        // Bug 27136: Enable error reporting in SDF when there is error for loading inline image (for errors like AttNotFound, NotImage, 404 etc.)
        // This change is temporary. We would like to get some real repro for the error to troubleshoot the issue
        if (isFeatureEnabled('mc-imageDiagnostics')) {
            logDiagnosticData(image, error, responseData);
        }
    }
}
